import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../apiConfig";
import FullscreenLoader from "../components/FullscreenLoader";
import {
  canAccessDashboardRoute,
  getRouteLockCopy,
  SUBSCRIPTION_ROUTE,
} from "../utils/planPermissions";
import {
  getPlanTierFromMySubscriptionResponse,
  isSubscriptionEffectivelyActiveFromResponse,
} from "../utils/subscriptionPlan";

// Lazy: PlanLockedPage pulls in Sidebar + Dashboard.css, which the public catalog must not download.
const PlanLockedPage = lazy(() => import("../components/PlanLockedPage"));

function readDashboardLanguage() {
  return (
    localStorage.getItem("dashboardLanguage") ||
    localStorage.getItem("catalogLanguage") ||
    (document.documentElement.dir === "rtl" ? "ar" : "en")
  );
}

export const TIER_CACHE_KEY = "kataloji_subscription_tier_v1";

function readCachedTier() {
  try {
    const raw = sessionStorage.getItem(TIER_CACHE_KEY);
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeCachedTier(tier) {
  try {
    sessionStorage.setItem(TIER_CACHE_KEY, String(tier));
  } catch {
    /* ignore */
  }
}

function isAbortError(err) {
  if (!err) return false;
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.name === "AbortError") {
    return true;
  }
  if (typeof axios.isCancel === "function" && axios.isCancel(err)) return true;
  return false;
}

export default function SubscriptionGuard() {
  const location = useLocation();
  const path = location.pathname || "";
  const search = location.search || "";

  const [state, setState] = useState(() => ({
    loading: true,
    expired: false,
    tier: readCachedTier() ?? 0,
    language: readDashboardLanguage(),
  }));

  useEffect(() => {
    const syncLang = () => setState((s) => ({ ...s, language: readDashboardLanguage() }));
    window.addEventListener("kataloji:language-changed", syncLang);
    return () => window.removeEventListener("kataloji:language-changed", syncLang);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      writeCachedTier(0);
      setState((s) => ({ ...s, loading: false, expired: false, tier: 0 }));
      return undefined;
    }

    const ac = new AbortController();
    let cancelled = false;

    const refreshSubscriptionAccess = async ({ showLoader = false } = {}) => {
      const hadCachedTier = readCachedTier() != null;
      if (showLoader && !hadCachedTier) {
        setState((s) => ({ ...s, loading: true }));
      }
      try {
        const res = await axios.get(`${API_URL}/subscription/my-subscription`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
          timeout: 25000,
        });
        if (cancelled) return;
        const msg = String(res.data?.message || "");
        const expired =
          res.data?.expired === true ||
          res.data?.admin_suspended === true ||
          (res.data?.success === false &&
            (msg.includes("SUBSCRIPTION_EXPIRED") ||
              msg.includes("SUBSCRIPTION_SUSPENDED")));
        const active = isSubscriptionEffectivelyActiveFromResponse(res.data);
        const tier = active ? getPlanTierFromMySubscriptionResponse(res.data) : 0;
        writeCachedTier(tier);
        setState((s) => ({
          ...s,
          loading: false,
          expired: Boolean(expired),
          tier,
        }));
      } catch (err) {
        if (isAbortError(err) || cancelled) return;
        const cached = readCachedTier();
        setState((s) => ({
          ...s,
          loading: false,
          expired: false,
          tier: cached ?? s.tier,
        }));
      }
    };

    refreshSubscriptionAccess({ showLoader: true });

    const onSubscriptionChanged = () => {
      refreshSubscriptionAccess({ showLoader: false });
    };
    window.addEventListener("kataloji:subscription-changed", onSubscriptionChanged);

    return () => {
      cancelled = true;
      ac.abort();
      window.removeEventListener("kataloji:subscription-changed", onSubscriptionChanged);
    };
  }, []);

  const access = useMemo(
    () => canAccessDashboardRoute(path, search, state.tier),
    [path, search, state.tier]
  );

  if (state.loading) {
    return <FullscreenLoader alt="Kataloji" />;
  }

  const onSetupRoute = path === "/setup" || path.startsWith("/setup/");

  if (
    state.expired &&
    !onSetupRoute &&
    path !== SUBSCRIPTION_ROUTE &&
    !path.startsWith(`${SUBSCRIPTION_ROUTE}/`)
  ) {
    return (
      <Navigate
        to={SUBSCRIPTION_ROUTE}
        replace
        state={{ subscriptionExpired: true, blockedFrom: path }}
      />
    );
  }

  if (!access.allowed && access.reason) {
    const copy = getRouteLockCopy(access.reason, state.language);
    return (
      <Suspense fallback={<FullscreenLoader alt="Kataloji" />}>
        <PlanLockedPage
          title={copy.title}
          body={copy.body}
          ctaLabel={copy.ctaLabel}
          language={state.language}
        />
      </Suspense>
    );
  }

  return <Outlet />;
}
